// src/components/AccessibilityMenu.jsx
import { useContext, useState } from "react";
import { SiteContext } from "../context/SiteContext";
import { 
  Plus, 
  Minus, 
  Sun, 
  Contrast, 
  Link, 
  Type, 
  RotateCcw, 
  Play, 
  Pause, 
  Square, 
  X
} from "lucide-react";
import "./AccessibilityMenu.css";

// 🧍 Símbolo da Pessoa em Pé (Acessibilidade)
function AccessibilityPersonIcon({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c.83 0 1.5.67 1.5 1.5S12.83 8 12 8s-1.5-.67-1.5-1.5S11.17 5 12 5zm4.5 6H13v7h-2v-7H7.5V9.5h9V11z"/>
    </svg>
  );
}

export default function AccessibilityMenu() {
  const { toggleContrast } = useContext(SiteContext);
  
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const [isGrayscale, setIsGrayscale] = useState(false);
  const [isHighlightLinks, setIsHighlightLinks] = useState(false);
  const [isReadableFont, setIsReadableFont] = useState(false);

  const changeFontSize = (delta) => {
    const newSize = Math.min(Math.max(fontSize + delta, 80), 140);
    setFontSize(newSize);
    document.documentElement.style.fontSize = `${newSize}%`;
  };

  const toggleGrayscale = () => {
    setIsGrayscale((prev) => {
      const next = !prev;
      if (next) document.body.classList.add("grayscale-mode");
      else document.body.classList.remove("grayscale-mode");
      return next;
    });
  };

  const toggleLinks = () => {
    setIsHighlightLinks((prev) => {
      const next = !prev;
      if (next) document.body.classList.add("highlight-links");
      else document.body.classList.remove("highlight-links");
      return next;
    });
  };

  const toggleReadableFont = () => {
    setIsReadableFont((prev) => {
      const next = !prev;
      if (next) document.body.classList.add("readable-font");
      else document.body.classList.remove("readable-font");
      return next;
    });
  };

  const resetAll = () => {
    setFontSize(100);
    document.documentElement.style.fontSize = "100%";
    
    setIsGrayscale(false);
    document.body.classList.remove("grayscale-mode");
    
    setIsHighlightLinks(false);
    document.body.classList.remove("highlight-links");
    
    setIsReadableFont(false);
    document.body.classList.remove("readable-font");

    document.body.classList.remove("high-contrast");
    
    stopSpeech();
  };

  const speakPage = () => {
    if (!('speechSynthesis' in window)) return;

    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      return;
    }

    window.speechSynthesis.cancel();
    
    const textToRead = document.querySelector("main") 
      ? document.querySelector("main").innerText 
      : document.body.innerText;

    const utterance = new SpeechSynthesisUtterance(textToRead.slice(0, 3000));
    utterance.lang = "pt-BR";
    window.speechSynthesis.speak(utterance);
  };

  const pauseSpeech = () => {
    if (window.speechSynthesis && window.speechSynthesis.speaking) {
      window.speechSynthesis.pause();
    }
  };

  const stopSpeech = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  };

  return (
    <>
      {/* Botão Flutuante Lateral no lado DIREITO */}
      <button 
        className="accessibility-btn-floating"
        onClick={() => setIsOpen(!isOpen)}
        title="Menu de Acessibilidade"
        aria-label="Abrir Menu de Acessibilidade"
      >
        <AccessibilityPersonIcon size={26} />
      </button>

      {/* Painel de Opções */}
      {isOpen && (
        <div className="accessibility-panel">
          <div className="accessibility-header">
            <span>Acessibilidade</span>
            <button className="accessibility-close-btn" onClick={() => setIsOpen(false)}>
              <X size={20} />
            </button>
          </div>

          <div className="accessibility-options">
            <button className="accessibility-option-btn" onClick={() => changeFontSize(10)}>
              <Plus size={18} /> Aumentar Fonte
            </button>

            <button className="accessibility-option-btn" onClick={() => changeFontSize(-10)}>
              <Minus size={18} /> Diminuir Fonte
            </button>

            <button className="accessibility-option-btn" onClick={toggleGrayscale}>
              <Sun size={18} /> {isGrayscale ? "Desativar Preto e Branco" : "Preto e Branco"}
            </button>

            <button className="accessibility-option-btn" onClick={toggleContrast}>
              <Contrast size={18} /> Inverter Cores / Alto Contraste
            </button>

            <button className="accessibility-option-btn" onClick={toggleLinks}>
              <Link size={18} /> Destacar Links
            </button>

            <button className="accessibility-option-btn" onClick={toggleReadableFont}>
              <Type size={18} /> {isReadableFont ? "Fonte Padrão" : "Fonte Legível"}
            </button>

            <button className="accessibility-option-btn" onClick={resetAll}>
              <RotateCcw size={18} /> Redefinir
            </button>

            {/* Leitor de Voz */}
            <div className="speech-reader-box">
              <p>Ler Página</p>
              <div className="speech-controls">
                <button className="speech-btn" onClick={speakPage} title="Iniciar / Retomar Leitura">
                  <Play size={16} />
                </button>
                <button className="speech-btn" onClick={pauseSpeech} title="Pausar Leitura">
                  <Pause size={16} />
                </button>
                <button className="speech-btn" onClick={stopSpeech} title="Parar Leitura">
                  <Square size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}